import { useState, useEffect } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { 
  ChevronDown, 
  ChevronUp, 
  PoundSterling, 
  DollarSign,
  Plus,
  Trash2,
  TrendingUp,
  Calculator,
  Target
} from 'lucide-react';
import { cn } from '../lib/utils';

const ValueTrackingForm = ({ 
  financialData, 
  operationalMetrics = [], 
  onChange, 
  readOnly = false,
  showForecast = false, // Only show forecast fields in project phase
  showActuals = false   // Only show actual fields post-implementation
}) => {
  const [settings, setSettings] = useState({ currency_symbol: '£', currency_code: 'GBP' });
  const [metricsLibrary, setMetricsLibrary] = useState([]);
  const [financialOpen, setFinancialOpen] = useState(true);
  const [operationalOpen, setOperationalOpen] = useState(true);

  useEffect(() => {
    fetchSettings();
    fetchMetricsLibrary();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch settings');
    }
  };

  const fetchMetricsLibrary = async () => {
    try {
      const response = await api.get('/metrics-library');
      setMetricsLibrary(response.data);
    } catch (error) {
      console.error('Failed to fetch metrics library');
    }
  };

  const CurrencyIcon = settings.currency_symbol === '$' ? DollarSign : PoundSterling;

  const handleFinancialChange = (section, field, value) => {
    const numValue = value === '' ? 0 : parseFloat(value) || 0;
    const newFinancial = {
      ...financialData,
      [section]: {
        ...(financialData?.[section] || {}),
        [field]: numValue
      }
    };
    onChange({ financial: newFinancial, operational: operationalMetrics });
  };

  const handleAddMetric = () => {
    const newMetric = {
      id: `temp-${Date.now()}`,
      metric_id: '',
      metric_name: '',
      unit: '',
      category: '',
      baseline_value: null,
      target_value: null,
      actual_value: null,
      notes: ''
    };
    onChange({ financial: financialData, operational: [...operationalMetrics, newMetric] });
  };

  const handleMetricSelect = (index, metricId) => {
    const selectedMetric = metricsLibrary.find(m => m.id === metricId);
    if (selectedMetric) {
      const updated = [...operationalMetrics];
      updated[index] = {
        ...updated[index],
        metric_id: selectedMetric.id,
        metric_name: selectedMetric.name,
        unit: selectedMetric.unit,
        category: selectedMetric.category
      };
      onChange({ financial: financialData, operational: updated });
    }
  };

  const handleMetricValueChange = (index, field, value) => {
    const updated = [...operationalMetrics];
    updated[index] = {
      ...updated[index],
      [field]: value === '' ? null : (field === 'notes' ? value : parseFloat(value))
    };
    onChange({ financial: financialData, operational: updated });
  };

  const handleRemoveMetric = (index) => {
    const updated = operationalMetrics.filter((_, i) => i !== index);
    onChange({ financial: financialData, operational: updated });
  };

  // Group metrics by category for dropdown
  const groupedMetrics = metricsLibrary.reduce((acc, metric) => {
    if (!acc[metric.category]) acc[metric.category] = [];
    acc[metric.category].push(metric);
    return acc;
  }, {});

  // Calculate totals for summary
  const calculateTotals = () => {
    const costs = financialData?.costs || {};
    const savings = financialData?.savings || {};
    
    const totalExpectedCost = (costs.one_off_expected || 0) + ((costs.ongoing_expected || 0) * 12);
    const totalForecastCost = (costs.one_off_forecast || 0) + ((costs.ongoing_forecast || 0) * 12);
    const totalActualCost = (costs.one_off_actual || 0) + ((costs.ongoing_actual || 0) * 12);
    
    const totalExpectedSavings = (savings.one_off_expected || 0) + ((savings.ongoing_expected || 0) * 12);
    const totalActualSavings = (savings.one_off_actual || 0) + ((savings.ongoing_actual || 0) * 12);
    
    const expectedROI = totalExpectedCost > 0 ? ((totalExpectedSavings - totalExpectedCost) / totalExpectedCost * 100) : 0;
    const actualROI = totalActualCost > 0 ? ((totalActualSavings - totalActualCost) / totalActualCost * 100) : 0;
    
    return {
      totalExpectedCost,
      totalForecastCost,
      totalActualCost,
      totalExpectedSavings,
      totalActualSavings,
      expectedROI,
      actualROI,
      expectedNetBenefit: totalExpectedSavings - totalExpectedCost,
      actualNetBenefit: totalActualSavings - totalActualCost
    };
  };

  const totals = calculateTotals();

  return (
    <div className="space-y-4">
      {/* Financial Value Tracking */}
      <Collapsible open={financialOpen} onOpenChange={setFinancialOpen}>
        <Card className="border-2 border-[#235894]/20">
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-[#235894]">
                  <CurrencyIcon className="w-5 h-5" />
                  Financial Value Tracking
                  <span className="text-xs font-normal text-red-500 ml-2">* Required</span>
                </CardTitle>
                {financialOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-6">
              {/* Summary Banner */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gradient-to-r from-[#235894]/5 to-[#26B8A7]/5 rounded-lg">
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Expected Net Benefit</p>
                  <p className={cn(
                    "text-lg font-bold",
                    totals.expectedNetBenefit >= 0 ? "text-emerald-600" : "text-red-600"
                  )}>
                    {settings.currency_symbol}{totals.expectedNetBenefit.toLocaleString()}/yr
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-slate-500 uppercase">Expected ROI</p>
                  <p className={cn(
                    "text-lg font-bold",
                    totals.expectedROI >= 0 ? "text-emerald-600" : "text-red-600"
                  )}>
                    {totals.expectedROI.toFixed(0)}%
                  </p>
                </div>
                {showActuals && (
                  <>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 uppercase">Actual Net Benefit</p>
                      <p className={cn(
                        "text-lg font-bold",
                        totals.actualNetBenefit >= 0 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {settings.currency_symbol}{totals.actualNetBenefit.toLocaleString()}/yr
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-xs text-slate-500 uppercase">Actual ROI</p>
                      <p className={cn(
                        "text-lg font-bold",
                        totals.actualROI >= 0 ? "text-emerald-600" : "text-red-600"
                      )}>
                        {totals.actualROI.toFixed(0)}%
                      </p>
                    </div>
                  </>
                )}
              </div>

              {/* Costs Section */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <Calculator className="w-4 h-4 text-red-500" />
                  Costs
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* One-off Costs */}
                  <div className="p-4 bg-red-50/50 rounded-lg space-y-3">
                    <p className="text-sm font-medium text-red-800">One-off Costs</p>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs text-slate-500">Expected ({settings.currency_symbol})</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{settings.currency_symbol}</span>
                          <Input
                            type="number"
                            value={financialData?.costs?.one_off_expected || ''}
                            onChange={(e) => handleFinancialChange('costs', 'one_off_expected', e.target.value)}
                            className="pl-7"
                            disabled={readOnly}
                            data-testid="cost-oneoff-expected"
                          />
                        </div>
                      </div>
                      {showForecast && (
                        <div>
                          <Label className="text-xs text-slate-500">Forecast ({settings.currency_symbol})</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{settings.currency_symbol}</span>
                            <Input
                              type="number"
                              value={financialData?.costs?.one_off_forecast || ''}
                              onChange={(e) => handleFinancialChange('costs', 'one_off_forecast', e.target.value)}
                              className="pl-7"
                              disabled={readOnly}
                            />
                          </div>
                        </div>
                      )}
                      {showActuals && (
                        <div>
                          <Label className="text-xs text-slate-500">Actual ({settings.currency_symbol})</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{settings.currency_symbol}</span>
                            <Input
                              type="number"
                              value={financialData?.costs?.one_off_actual || ''}
                              onChange={(e) => handleFinancialChange('costs', 'one_off_actual', e.target.value)}
                              className="pl-7"
                              disabled={readOnly}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ongoing Costs */}
                  <div className="p-4 bg-red-50/50 rounded-lg space-y-3">
                    <p className="text-sm font-medium text-red-800">Ongoing Costs (per month)</p>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs text-slate-500">Expected ({settings.currency_symbol}/mo)</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{settings.currency_symbol}</span>
                          <Input
                            type="number"
                            value={financialData?.costs?.ongoing_expected || ''}
                            onChange={(e) => handleFinancialChange('costs', 'ongoing_expected', e.target.value)}
                            className="pl-7"
                            disabled={readOnly}
                            data-testid="cost-ongoing-expected"
                          />
                        </div>
                      </div>
                      {showForecast && (
                        <div>
                          <Label className="text-xs text-slate-500">Forecast ({settings.currency_symbol}/mo)</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{settings.currency_symbol}</span>
                            <Input
                              type="number"
                              value={financialData?.costs?.ongoing_forecast || ''}
                              onChange={(e) => handleFinancialChange('costs', 'ongoing_forecast', e.target.value)}
                              className="pl-7"
                              disabled={readOnly}
                            />
                          </div>
                        </div>
                      )}
                      {showActuals && (
                        <div>
                          <Label className="text-xs text-slate-500">Actual ({settings.currency_symbol}/mo)</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{settings.currency_symbol}</span>
                            <Input
                              type="number"
                              value={financialData?.costs?.ongoing_actual || ''}
                              onChange={(e) => handleFinancialChange('costs', 'ongoing_actual', e.target.value)}
                              className="pl-7"
                              disabled={readOnly}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Savings Section */}
              <div>
                <h4 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  Savings / Benefits
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* One-off Savings */}
                  <div className="p-4 bg-emerald-50/50 rounded-lg space-y-3">
                    <p className="text-sm font-medium text-emerald-800">One-off Savings</p>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs text-slate-500">Expected ({settings.currency_symbol})</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{settings.currency_symbol}</span>
                          <Input
                            type="number"
                            value={financialData?.savings?.one_off_expected || ''}
                            onChange={(e) => handleFinancialChange('savings', 'one_off_expected', e.target.value)}
                            className="pl-7"
                            disabled={readOnly}
                            data-testid="savings-oneoff-expected"
                          />
                        </div>
                      </div>
                      {showActuals && (
                        <div>
                          <Label className="text-xs text-slate-500">Actual ({settings.currency_symbol})</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{settings.currency_symbol}</span>
                            <Input
                              type="number"
                              value={financialData?.savings?.one_off_actual || ''}
                              onChange={(e) => handleFinancialChange('savings', 'one_off_actual', e.target.value)}
                              className="pl-7"
                              disabled={readOnly}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Ongoing Savings */}
                  <div className="p-4 bg-emerald-50/50 rounded-lg space-y-3">
                    <p className="text-sm font-medium text-emerald-800">Ongoing Savings (per month)</p>
                    <div className="space-y-2">
                      <div>
                        <Label className="text-xs text-slate-500">Expected ({settings.currency_symbol}/mo)</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{settings.currency_symbol}</span>
                          <Input
                            type="number"
                            value={financialData?.savings?.ongoing_expected || ''}
                            onChange={(e) => handleFinancialChange('savings', 'ongoing_expected', e.target.value)}
                            className="pl-7"
                            disabled={readOnly}
                            data-testid="savings-ongoing-expected"
                          />
                        </div>
                      </div>
                      {showActuals && (
                        <div>
                          <Label className="text-xs text-slate-500">Actual ({settings.currency_symbol}/mo)</Label>
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">{settings.currency_symbol}</span>
                            <Input
                              type="number"
                              value={financialData?.savings?.ongoing_actual || ''}
                              onChange={(e) => handleFinancialChange('savings', 'ongoing_actual', e.target.value)}
                              className="pl-7"
                              disabled={readOnly}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>

      {/* Operational Metrics */}
      <Collapsible open={operationalOpen} onOpenChange={setOperationalOpen}>
        <Card>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-slate-50 transition-colors">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#26B8A7]" />
                  Operational Metrics
                  <span className="text-xs font-normal text-slate-400 ml-2">Optional</span>
                </CardTitle>
                {operationalOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </div>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-500">
                Track non-financial performance metrics. Select from the standard library to ensure consistency.
              </p>

              {operationalMetrics.map((metric, index) => (
                <div 
                  key={metric.id} 
                  className="p-4 bg-slate-50 rounded-lg space-y-3"
                  data-testid={`operational-metric-${index}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-3">
                      {/* Metric Selection */}
                      <div className="md:col-span-2">
                        <Label className="text-xs text-slate-500">Metric</Label>
                        <Select
                          value={metric.metric_id}
                          onValueChange={(v) => handleMetricSelect(index, v)}
                          disabled={readOnly}
                        >
                          <SelectTrigger data-testid={`metric-select-${index}`}>
                            <SelectValue placeholder="Select metric..." />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.entries(groupedMetrics).map(([category, metrics]) => (
                              <div key={category}>
                                <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 bg-slate-100">
                                  {category}
                                </div>
                                {metrics.map((m) => (
                                  <SelectItem key={m.id} value={m.id}>
                                    {m.name} ({m.unit})
                                  </SelectItem>
                                ))}
                              </div>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Unit (Auto-filled) */}
                      <div>
                        <Label className="text-xs text-slate-500">Unit</Label>
                        <Input
                          value={metric.unit || ''}
                          disabled
                          className="bg-slate-100"
                        />
                      </div>

                      {/* Category Badge */}
                      {metric.category && (
                        <div className="flex items-end">
                          <span className={cn(
                            "px-2 py-1 text-xs rounded-full",
                            metric.category === 'Efficiency' && "bg-blue-100 text-blue-800",
                            metric.category === 'Quality' && "bg-purple-100 text-purple-800",
                            metric.category === 'Service & Experience' && "bg-amber-100 text-amber-800",
                            metric.category === 'People & Risk' && "bg-red-100 text-red-800"
                          )}>
                            {metric.category}
                          </span>
                        </div>
                      )}
                    </div>

                    {!readOnly && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemoveMetric(index)}
                        className="text-red-500 hover:text-red-700 mt-5"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>

                  {metric.metric_id && (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Baseline ({metric.unit})</Label>
                        <Input
                          type="number"
                          value={metric.baseline_value ?? ''}
                          onChange={(e) => handleMetricValueChange(index, 'baseline_value', e.target.value)}
                          placeholder="Current state"
                          disabled={readOnly}
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">Target ({metric.unit})</Label>
                        <Input
                          type="number"
                          value={metric.target_value ?? ''}
                          onChange={(e) => handleMetricValueChange(index, 'target_value', e.target.value)}
                          placeholder="Expected"
                          disabled={readOnly}
                        />
                      </div>
                      {showActuals && (
                        <div>
                          <Label className="text-xs text-slate-500">Actual ({metric.unit})</Label>
                          <Input
                            type="number"
                            value={metric.actual_value ?? ''}
                            onChange={(e) => handleMetricValueChange(index, 'actual_value', e.target.value)}
                            placeholder="Achieved"
                            disabled={readOnly}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {!readOnly && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleAddMetric}
                  className="w-full border-dashed"
                  data-testid="add-metric-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Operational Metric
                </Button>
              )}

              {operationalMetrics.length === 0 && (
                <p className="text-center text-slate-400 py-4">
                  No operational metrics added yet
                </p>
              )}
            </CardContent>
          </CollapsibleContent>
        </Card>
      </Collapsible>
    </div>
  );
};

export default ValueTrackingForm;
